import React, { useContext } from "react";
import { ExpandMore, ChevronRight, Edit } from "@mui/icons-material";
import { Box, Collapse } from "@mui/material";
import { IGroupInfo } from "@home-health-notify/domain";
import { ILabRequisitionData } from "@home-health-notify/domain";
import {
  LabRequisitionsProvider,
  useLabRequisitionListItem,
  UserDirectoryContext,
  useTranslation,
} from "hhn-commons";
import { LeftDrawerContext } from "../../../context/LeftDrawerContext";
import NewLabRequisition from "../../../labs/NewLabRequisition";
import LabOrders from "./LabOrders";
import {
  RequisitionContainer,
  RequisitionCard,
  RequisitionHeader,
  RequisitionContent,
  TestNameText,
  EditButton,
  ExpandedContent,
  RequisitionListItem,
  ExpandIconButton,
  RequisitionStatusChip,
} from "./LabRequisitionListItem.style";
import {
  MetadataRow,
  MetadataItem,
  CalendarTodayIcon,
  CollectionDateTypography,
  LoopIcon,
  RecurringTypography,
  BiotechIcon,
  LabNameTypography,
  SubmittedByTypography,
  SubmittedByIcon,
  SubmittedByLabelTypography,
} from "./LabCommon.style";

type OrderListItemType = {
  requisition: ILabRequisitionData;
  groupInfo: IGroupInfo;
};

const LabRequisitionListItem = ({
  requisition,
  groupInfo,
}: OrderListItemType) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "labRequisition" });

  const { openDrawer } = useContext(LeftDrawerContext);
  const { userDirectory } = useContext(UserDirectoryContext);

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    openDrawer(
      <LabRequisitionsProvider>
        <NewLabRequisition
          groupInfo={groupInfo}
          labRequisitionId={requisition.id}
          key={requisition.id}
          readonly={true}
        ></NewLabRequisition>
      </LabRequisitionsProvider>,
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "PENDING":
        return "info";
      case "ORDER_PLACED":
        return "success";
      case "FAILED":
        return "error";
      default:
        return "default";
    }
  };

  const {
    toggleExpand,
    isExpanded,
    requisitionPanels,
    readableCollectionDate,
  } = useLabRequisitionListItem(requisition);

  const onToggleExpand = async (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleExpand();
  };

  return (
    <RequisitionListItem data-testid="lab-requisition-list-item">
      <RequisitionContainer>
        <RequisitionCard onClick={onToggleExpand}>
          <RequisitionHeader>
            <ExpandIconButton size="small" onClick={onToggleExpand}>
              {isExpanded ? (
                <ExpandMore fontSize="small" />
              ) : (
                <ChevronRight fontSize="small" />
              )}
            </ExpandIconButton>
            <RequisitionContent>
              <TestNameText variant="subtitle2">
                {requisitionPanels}
              </TestNameText>
              <MetadataRow
                direction="row"
                spacing={1.5}
                sx={{ alignItems: "center" }}
              >
                <MetadataItem>
                  <CalendarTodayIcon />
                  <CollectionDateTypography variant="caption">
                    {readableCollectionDate}
                  </CollectionDateTypography>
                </MetadataItem>
                <MetadataItem>
                  <BiotechIcon />
                  <LabNameTypography variant="caption">
                    {requisition.labName}
                  </LabNameTypography>
                </MetadataItem>
                {requisition.specimenInformation.requisitionType ===
                  "Standing Order" && (
                  <MetadataItem>
                    <LoopIcon />
                    <RecurringTypography variant="caption">
                      {tNs("recurring")}
                    </RecurringTypography>
                  </MetadataItem>
                )}
              </MetadataRow>
              {requisition.submittedBy && (
                <MetadataRow
                  direction="row"
                  spacing={1.5}
                  sx={{ alignItems: "center" }}
                >
                  <MetadataItem>
                    <SubmittedByIcon />
                    <SubmittedByLabelTypography variant="caption">
                      {tNs("submittedBy")}
                    </SubmittedByLabelTypography>
                    <SubmittedByTypography variant="caption">
                      {userDirectory.getUserInfo(requisition.submittedBy).name}
                    </SubmittedByTypography>
                  </MetadataItem>
                </MetadataRow>
              )}
              {requisition.status && (
                <Box>
                  <RequisitionStatusChip
                    label={requisition.status}
                    color={getStatusColor(requisition.status) as any}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              )}
            </RequisitionContent>
            <EditButton onClick={handleEdit}>
              <Edit fontSize="small" />
            </EditButton>
          </RequisitionHeader>
          <Collapse in={isExpanded}>
            <ExpandedContent>
              <LabOrders requisition={requisition} />
            </ExpandedContent>
          </Collapse>
        </RequisitionCard>
      </RequisitionContainer>
    </RequisitionListItem>
  );
};

export default LabRequisitionListItem;
